"use client"

import { Search, X } from "lucide-react"
import { useRouter } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Database } from "supabase/database.types"
import useSWR from "swr"

import { TrackedButton, TrackedLink } from "@/components/analytics"
import useAnalytics from "@/hooks/useAnalytics"
import { createClient } from "@/lib/supabase"

import Icon, { getSubjectIconColor } from "../Icon"

interface EnhancedSearchProps {
  className?: string
  placeholder?: string
}

interface SubjectSearchResult {
  color: Database["public"]["Enums"]["color"]
  icon: Database["public"]["Enums"]["icon"]
  name: string
  parentName: null | string
  relevance: number
  slug: string
}

const fetcher = async () => {
  const supabase = createClient()
  const { data, error } = await supabase
    .from("subjects")
    .select(
      "slug, name, description, tier, icon, color, parent_slug, hidden, canonical_slug"
    )
    .eq("hidden", false)
    .order("name", { ascending: true })
    .throwOnError()

  if (error) throw error
  return data
}

export default function EnhancedSearch({
  className = "",
  placeholder = "Search subjects..."
}: EnhancedSearchProps) {
  const [query, setQuery] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const { data } = useSWR(isFocused ? "/api/subjects" : null, fetcher)
  const { trackEvent } = useAnalytics()

  // Transform to the format expected by Navigation/EnhancedSearch
  const subjects = useMemo(
    () =>
      data?.map((subject) => ({
        color: subject.color,
        icon: subject.icon,
        name: subject.name,
        parentName:
          data.find((s) => s.slug === subject.parent_slug)?.name || null,
        slug: subject.slug
      })) ?? [],
    [data]
  )

  const [results, setResults] = useState<SubjectSearchResult[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()

  const trackSearch = useCallback(
    (query: string) => {
      void trackEvent({
        action: "Search Entered",
        category: "page",
        label: "Search Input",
        value: query
      })
    },
    [trackEvent]
  )

  const trackQuery = useCallback(
    (query: string) => {
      void trackEvent({
        action: "Search Entered",
        category: "page",
        label: "Search Input",
        value: query
      })
    },
    [trackEvent]
  )

  // Filter subjects based on query
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      setSelectedIndex(-1)
      return
    }

    const filtered = subjects
      .filter((subject) =>
        subject.name.toLowerCase().includes(query.toLowerCase())
      )
      .map((subject) => ({
        color: subject.color,
        icon: subject.icon,
        name: subject.name,
        parentName: subject.parentName,
        relevance: calculateRelevance(subject.name, query),
        slug: subject.slug
      }))
      .sort((a, b) => b.relevance - a.relevance)
      .slice(0, 8) // Limit to 8 results

    setResults(filtered)
    setSelectedIndex(-1)
  }, [query, subjects])

  const calculateRelevance = (subject: string, query: string): number => {
    const subjectLower = subject.toLowerCase()
    const queryLower = query.toLowerCase()

    // Exact match gets highest relevance
    if (subjectLower === queryLower) return 1.0

    // Starts with query gets high relevance
    if (subjectLower.startsWith(queryLower)) return 0.9

    // Contains query gets medium relevance
    if (subjectLower.includes(queryLower)) return 0.7

    // Partial word match gets lower relevance
    const subjectWords = subjectLower.split(/\s+/)
    const queryWords = queryLower.split(/\s+/)

    const wordMatches = queryWords.filter((word) =>
      subjectWords.some((subjectWord) => subjectWord.includes(word))
    )

    return (wordMatches.length / queryWords.length) * 0.5
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1))
        break
      case "Enter":
        e.preventDefault()
        trackSearch(query)
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          // Navigate to the selected result
          router.push(`/subjects/${results[selectedIndex].slug}`)
          setIsFocused(false)
          setQuery("")
          setResults([])
          setSelectedIndex(-1)
        } else if (results.length > 0) {
          // If no item is selected, navigate to the first one
          router.push(`/subjects/${results[0].slug}`)
          setIsFocused(false)
          setQuery("")
          setResults([])
          setSelectedIndex(-1)
        }
        break
      case "Escape":
        setIsFocused(false)
        setQuery("")
        setResults([])
        setSelectedIndex(-1)
        break
    }
  }

  const handleClear = () => {
    setQuery("")
    setResults([])
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  return (
    <div className={`relative flex-1 hidden sm:block ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <Search
          aria-hidden="true"
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400"
          focusable="false"
        />
        <label className="sr-only" htmlFor="global-nav-search">
          Search subjects
        </label>
        <input
          className="w-full pl-10 pr-10 py-[9px] bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
          id="global-nav-search"
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          onBlurCapture={() => trackQuery(query)}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          type="text"
          value={query}
        />

        {/* Clear Button */}
        {query && (
          <TrackedButton
            action="Search Cleared"
            category="page"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            label="Clear Search"
            onClick={handleClear}
            value="X Button"
          >
            <X className="w-4 h-4 text-slate-400" />
          </TrackedButton>
        )}
      </div>

      {/* Results Dropdown */}
      {isFocused && (query || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto animate-fade-in-down animation-duration-200">
          {results.length > 0 ? (
            <div className="p-2">
              {results.map((result, index) => (
                <TrackedLink
                  action="Search Result Clicked"
                  category="page"
                  className={`block p-3 rounded-lg transition-colors cursor-pointer animate-fade-in-up animation-duration-200 ${
                    index === selectedIndex
                      ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
                  href={`/subjects/${result.slug}`}
                  key={result.slug}
                  label={query}
                  onClick={() => {
                    setIsFocused(false)
                    setQuery("")
                    setResults([])
                    setSelectedIndex(-1)
                  }}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      className={`${getSubjectIconColor(
                        result.color
                      )} w-4 h-4 flex-shrink-0`}
                      icon={result.icon}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 dark:text-white text-sm">
                        {result.name}
                      </div>
                      {result.parentName && (
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {result.parentName}
                        </div>
                      )}
                    </div>
                    {index === selectedIndex && (
                      <div className="text-xs bg-emerald-200 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-full">
                        Press Enter
                      </div>
                    )}
                  </div>
                </TrackedLink>
              ))}
            </div>
          ) : query && !results.length ? (
            <div className="p-4 text-center text-slate-500 dark:text-slate-400">
              No subjects found for &quot;{query}&quot;
            </div>
          ) : null}
        </div>
      )}

      {/* Backdrop */}
      {isFocused && (query || results.length > 0) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsFocused(false)
            setQuery("")
            setResults([])
            setSelectedIndex(-1)
          }}
        />
      )}
    </div>
  )
}
